import { SkywardException } from "#skyward_error";
import { loadSkywardModuleByNamespace } from "#skyward_global";
import { computed, ComputedRef, reactive, readonly, SetupContext, shallowReadonly } from "vue";
import { logAjaxError } from "../../../../skyward.base.ajaxError";
import FieldTree from "../../../../skyward.ui.browse.fieldTree";
import { getCallbackStore, getCallbackStoreFacade } from "../../../../skyward.ui.callbackStore";
import { CallbackStore, Dictionary } from "../../../../types";
import { Deferred } from "../../../../types/util/deferred";
import { computedMemo } from "../../../core/vueHelpers";
import { type RowID } from "../../browse/store/types";
import { coreStoreFeatures } from "./features";
import { Component, isSkyVueComponent } from "./features/components";
import { ErrorStates, FetchDataBehavior, FetchOptions, RowData, SkyVueData, State, StateProps, Store, StoreCollection } from "./types";

const storeCollection: StoreCollection = {};
(window.skyward as any).skyVue ??= {};
(window.skyward as any).skyVue._storeCollection = shallowReadonly(storeCollection);

const writableParent = Symbol("writableParent");

type StateInternal = State<unknown, unknown, unknown> &
{
    [writableParent]: StateInternal,
    children: StateInternal[],
};

export function registerStore<TFeatures, TSetupStore extends object, TSetupState, TNonPageableData extends object = Dictionary<string, any>>
    (key: Symbol, features: TFeatures, setupStore: () => TSetupStore, setupState: (id: string, storeState: State<TFeatures, TSetupStore, TSetupState, {}, TNonPageableData>) => TSetupState, onDelete?: (id: string) => void): Store<TFeatures, TSetupStore, TSetupState>
{
    const stringKey = String(key);
    if (stringKey in storeCollection)
    {
        window.skyward.error.reportException(new SkywardException("Store has already been registered.", `The store with the key of '${key}' has already been registered.`));
    }

    const store: Store<TFeatures, TSetupStore, TSetupState> = Object.assign(Object.create(setupStore() ?? Object.prototype),
    {
        key: stringKey,
        symbolKey: key,
        setup: setupState,
        features: Object.assign({}, features, coreStoreFeatures),
        states: new Map<string, State<TFeatures, TSetupState, any, any, any>>(),
        awaitedStates: new Map<string, Deferred<State<TFeatures, TSetupState, any, any, any>>>(),
        addState,
        getState,
        getStateAsync,
        hasState,
        deleteState,
    });

    storeCollection[stringKey] = store;

    return store;

    function addState<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any> = {}>(
        { skyVueData, parent }: StateProps,
        context: SetupContext
    )
    {
        const id = skyVueData.id;

        if (store.states.has(id))
        {
            throw new Error(`The ${nameof(id)} of '${id}' has already been added.`);
        }

        let lastRequest: JQuery.jqXHR | undefined;
        let computedLength: ComputedRef<number>;
        let identifyingFieldValues: ComputedRef<unknown>;
        let computedPostData: ComputedRef<Record<string, any>>;
        let computedErrorState: ComputedRef<ErrorStates>;
        let computedErrorUserMessage: ComputedRef<string>;
        let initialErrorUserMessage = "";
        const hasFatalSecurityError = getFatalSecurityError(skyVueData);

        function getFatalSecurityError(skyVueData: SkyVueData): boolean
        {
            if (skyVueData.features.securityData)
            {
                if (skyVueData.features.securityData.errorType !== "OK")
                {
                    initialErrorUserMessage = skyVueData.features.securityData.userMessage;
                    return true;
                }
                return false;
            }
            return skyVueData.features.components.some((c: Component) => isSkyVueComponent(c) && getFatalSecurityError(c.skyVueData!));
        }

        const onAfterFetch: CallbackStore<() => void> = getCallbackStore();
        const onRefresh: CallbackStore<() => void> = getCallbackStore();
        let timeout: undefined | NodeJS.Timeout = undefined;
        const state =
        {
            features: reactive($.extend(true, {}, skyVueData.features)),
            data: reactive(
            {
                isLoaded: false,
                isInitiallyLoaded: false,
                get hasNoData() { return hasNoData.value === true; },
                get isErrored() { return computedErrorState.value === ErrorStates.ServerError; },
                values: [] as TDataType[],
                nonPageableValues: {} as TNonPageable,
                get length() { return computedLength.value; },
                getIndexByIdentifyingField,
                getRowByIdentifyingField,
                get identifyingFieldValues() { return identifyingFieldValues.value; },
                fetch: (fetchDataBehavior: FetchDataBehavior | FetchOptions, postData?: Record<string, any>) =>
                {
                    // debounce the call to fetch to work around multiple calls being generated in a single Vue rendering flow
                    clearTimeout(timeout);
                    return new Promise((resolve) =>
                    {
                        timeout = setTimeout(() =>
                        {
                            resolve(fetch(fetchDataBehavior, postData));
                        }, 0);
                    });
                },
                onAfterFetch: getCallbackStoreFacade(onAfterFetch),
                get canRefresh() { return !hasFatalSecurityError; },
                get postData() { return computedPostData.value; },
            }),
            hashes: $.extend(true, {}, skyVueData.hashes ?? {}),
            parent: parent ? readonly(parent) : undefined,
            [writableParent]: parent, // private field
            children: reactive([] as State<unknown, unknown, unknown>[]),
            get errorState() { return computedErrorState.value; },
            get errorUserMessage() { return computedErrorUserMessage.value; },
            refresh: refresh,
            onRefresh: getCallbackStoreFacade(onRefresh),
            get storeKey() { return store.key; },
            get stateId() { return id; },
        };

        computedLength = computed(() =>
        {
            return state.data.values?.length ?? 0;
        });

        identifyingFieldValues = computed(() =>
        {
            const idKey = state.features.dataSource?.identifierField;
            return state.data.values?.map(row => row[idKey]) ?? [];
        });

        computedErrorState = computed(getErrorState);
        computedErrorUserMessage = computed(getUserErrorMessage);
        computedPostData = computedMemo(postDataGetter);

        const thisStore = store.setup(id, state);

        const thisState = Object.defineProperties({}, {
            ...Object.getOwnPropertyDescriptors(state),
            ...Object.getOwnPropertyDescriptors(thisStore),
        }) as State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>;

        if (store.awaitedStates.has(id))
        {
            var awaitedState = store.awaitedStates.get(id);
            if (awaitedState !== undefined)
            {
                awaitedState?.resolve(thisState);
            }
            store.awaitedStates.delete(id);
        }

        const hasNoData = computed(() => state.data.isLoaded && state.data.isInitiallyLoaded && !(state.data?.nonPageableValues?.series?.length > 0));

        store.states.set(id, thisState);
        (thisState as unknown as StateInternal)[writableParent]?.children?.push(thisState as unknown as StateInternal);
        context.expose({ state: thisState });

        return thisState;

        function getIndexByIdentifyingField(id?: RowID | null): number
        {
            const index = thisState.data.identifyingFieldValues.indexOf(id);
            if (index !== -1 || id === null || id === undefined)
            {
                return index;
            }

            const idAsString = String(id);
            return thisState.data.identifyingFieldValues.findIndex(value => String(value) === idAsString);
        }

        function getRowByIdentifyingField(id?: RowID | null): TDataType | undefined
        {
            const index = getIndexByIdentifyingField(id);
            return index !== -1 ? thisState.data.values[index] : undefined;
        }

        async function fetch(fetchOptionsParam: FetchDataBehavior | FetchOptions, postData?: Record<string, any>)
        {
            if (!thisState.features.dataSource || thisState.errorState === ErrorStates.FatalNotSecure)
            {
                thisState.data.isLoaded = true;
                thisState.data.isInitiallyLoaded = true;
                return;
            }
            const newPostData = { ...postData };

            let fetchOptions: FetchOptions;
            const defaultOptions = { ajaxSettings: {} };
            if (typeof fetchOptionsParam === "object")
            {
                fetchOptions = { ...defaultOptions, ...fetchOptionsParam };
            }
            else
            {
                fetchOptions = { ...defaultOptions, fetchDataBehavior: fetchOptionsParam };
            }

            const isReplaceFetch = fetchOptions.fetchDataBehavior === FetchDataBehavior.Replace;

            // Needs to be done before getting postdata, so we don't use replaced values for paging data
            if (isReplaceFetch)
            {
                thisState.data.values = [] as any;
            }

            Object.assign(newPostData, getPostData());
            thisState.data.isLoaded = false;
            if (lastRequest && lastRequest.state() === "pending")
            {
                lastRequest.abort();
            }

            const currentRequest = window.jQuery.ajax(
                {
                    suppressErrors: (xhr) => xhr.status === 500 || xhr.status === 403 && xhr.getResponseHeader("X-Forbidden-Reason") !== "SessionChanged",
                    activityIndicator: { silent: isReplaceFetch },
                    ...fetchOptions.ajaxSettings,
                    skyvueKey: stringKey,
                    skyvueId: id,
                    url: window.skyward.global.virtualDir + "SkySys/VueData/GetData/",
                    type: "POST",
                    // allow intercepting skyvue requests via ajaxPrefilter, without adding a global filter
                    dataType: "skyvuefetch",
                    converters:
                    {
                        "json skyvuefetch": function (result)
                        {
                            return result;
                        }
                    },
                    cache: false,
                    data: newPostData,
                }
            );
            lastRequest = currentRequest;
            try
            {
                const data: RowData<TDataType> = await currentRequest;
                const transposed = transposeNestedData(data);

                await loadSkyVueItemDependencies(transposed.nonPageableValues?.NamespaceDependencies);
                switch (fetchOptions.fetchDataBehavior)
                {
                    case FetchDataBehavior.Replace:
                        thisState.data.values = transposed.values ?? [];
                        thisState.data.nonPageableValues = transposed.nonPageableValues ?? {};
                        break;
                    case FetchDataBehavior.Append:
                    case FetchDataBehavior.Prepend:
                        thisState.data.values = getDistinctRowData(thisState.features.dataSource.identifierField, thisState.data.values, transposed.values, fetchOptions.fetchDataBehavior);
                        if (transposed.nonPageableValues)
                        {
                            Object.assign(thisState.data.nonPageableValues, transposed.nonPageableValues);
                        }
                        break;
                    default:
                        throw new Error(`The ${nameof(FetchDataBehavior)} of '${fetchOptions.fetchDataBehavior}' is not supported in this context.`);
                }

                thisState.features.securityData.errorType = "OK";
                thisState.features.securityData.securityMessage = "";

                return transposed;
            }
            catch (e)
            {
                if (currentRequest.statusText === "abort")
                {
                    return {};
                }
                const defaultErrorMessage = "An error occurred and the browse could not be loaded.";

                switch (currentRequest.status)
                {
                    case 403:
                        const data = currentRequest.responseJSON as typeof thisState.features.securityData;
                        // Clear data without overwriting `values` or `nonPageableValues`, in case they have been cached somewhere
                        thisState.data.values.length = 0;
                        Object.keys(thisState.data.nonPageableValues).forEach(value => delete thisState.data.nonPageableValues[value]);
                        if (currentRequest.getResponseHeader("X-Forbidden-Reason") === "SessionChanged")
                        {
                            thisState.features.securityData.errorType = "NoSecurity";
                            thisState.features.securityData.securityMessage = currentRequest.responseText;
                        }
                        else
                        {
                            thisState.features.securityData.errorType = data.errorType ?? "InternalError";
                            thisState.features.securityData.securityMessage = data.securityMessage ?? defaultErrorMessage;
                        }
                        break;

                    default:
                        if (currentRequest.status === 500)
                        {
                            logAjaxError(currentRequest);
                        }
                        thisState.features.securityData.errorType = "InternalError";
                        thisState.features.securityData.securityMessage = defaultErrorMessage;
                        break;
                }
                return {};
            }
            finally
            {
                // `abort` is if we abort the request, `error` is if the connection fails due to a timeout, navigation, etc
                if (currentRequest.statusText !== "abort" && currentRequest.statusText !== "error")
                {
                    onAfterFetch.dispatchCallbacks();
                    context.emit("afterFetch", thisState);
                    thisState.data.isLoaded = true;
                    thisState.data.isInitiallyLoaded = true;
                }
            }

            function transposeNestedData(newRowData: RowData<TDataType>): { values: TDataType[], nonPageableValues: TNonPageable }
            {
                const tree = new FieldTree(newRowData);
                return tree.next().value as any;
            }

            function getDistinctRowData(
                distinctField: string,
                existingRowData: TDataType[] | undefined,
                newRowData: TDataType[],
                rowDataBehavior: FetchDataBehavior): TDataType[]
            {
                if (!existingRowData)
                {
                    return newRowData;
                }

                let existingRowDataClone = existingRowData?.slice() ?? [];
                // find the distinct fields that are present in both existingRowDataClone and newRowData
                existingRowDataClone = existingRowDataClone?.filter(existingRow => newRowData.findIndex(newRow => newRow[distinctField] === existingRow[distinctField]) === -1);

                switch (rowDataBehavior)
                {
                    case FetchDataBehavior.Append:
                        existingRowDataClone.push(...newRowData);
                        break;
                    case FetchDataBehavior.Prepend:
                        existingRowDataClone.unshift(...newRowData);
                        break;
                    default:
                        throw `The ${nameof(FetchDataBehavior)} of '${rowDataBehavior}' is not supported in this context.`;
                }
                return existingRowDataClone;
            }

            async function loadSkyVueItemDependencies(namespaceDependencies: string[] | undefined): Promise<any>
            {
                if (!Array.isArray(namespaceDependencies))
                {
                    return;
                }

                const namespaces = new Set<string>();
                for (let i = 0; i < namespaceDependencies.length; i++)
                {
                    namespaces.add(namespaceDependencies[i].split('.')[0]);
                }

                let promises = [];
                for (let namespace of namespaces)
                {
                    promises.push(loadSkywardModuleByNamespace(namespace));
                }

                return Promise.all(promises);
            }
        }

        function getErrorState(): ErrorStates
        {
            if (hasFatalSecurityError)
            {
                return ErrorStates.FatalNotSecure;
            }
            if (!state.features.dataSource)
            {
                for (const child of state.children)
                {
                    if (child.errorState !== ErrorStates.OK)
                    {
                        return child.errorState;
                    }
                }
            }
            else if (state.features.securityData.errorType !== "OK")
            {
                switch (state.features.securityData.errorType)
                {
                    case "InternalError": return ErrorStates.ServerError;
                    case "NoSecurity": return ErrorStates.NotSecure;
                    default: throw new Error("not implemented");
                }
            }
            return ErrorStates.OK;
        }

        function postDataGetter()
        {
            return getPostData("pagingType"); // exclude pagingType since it depends on state.data. Otherwise, reactivity will result in an infinite loop of fetch() calls
        }

        function getPostData(excludedFeature?: string | undefined): Record<string, any>
        {
            let newPostData: Record<string, any> = {};
            for (const [featureName, feature] of Object.entries(store.features))
            {
                if (typeof feature.getPostData === "function" && featureName !== excludedFeature)
                {
                    const features = thisState.features as Record<string, any>;
                    if (features[featureName])
                    {
                        Object.assign(newPostData, feature.getPostData(features[featureName], thisState));
                    }
                }
            }

            return newPostData;
        }

        function getUserErrorMessage()
        {
            return initialErrorUserMessage || state.features.securityData?.userMessage;
        }

        async function refresh()
        {
            if (!state.data.canRefresh)
            {
                return Promise.resolve();
            }

            state.features.securityData =
            {
                errorType: "OK",
                securityMessage: "",
                userMessage: "",
            };

            if (state.features.dataSource)
            {
                await state.data.fetch(FetchDataBehavior.Replace);
            }
            else
            {
                await Promise.all(state.children.map(child => child.refresh()));
            }
            onRefresh.dispatchCallbacks();
        }
    }

    function getState<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any>>(id: string)
    {
        if (!store.states.has(id))
        {
            throw `Could not retrieve store '${id}': Invalid ID`;
        }
        return store.states.get(id)! as State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>;
    }

    function getStateAsync<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any> = {}>(id: string):
        Promise<State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>>
    {
        if (store.hasState(id))
        {
            return Promise.resolve(store.getState(id));
        }

        if (store.awaitedStates.has(id))
        {
            return store.awaitedStates.get(id)!;
        }

        const deferred = new Deferred<State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>>();
        store.awaitedStates.set(id, deferred);
        return deferred;
    }

    function hasState(id: string)
    {
        return !!store.states.get(id);
    }

    function deleteState(id: string): void
    {
        if (typeof onDelete === "function")
        {
            onDelete(id);
        }

        if (store.awaitedStates.has(id))
        {
            var awaitedState = store.awaitedStates.get(id);
            if (awaitedState !== undefined)
            {
                awaitedState?.reject("deleteState");
            }
            store.awaitedStates.delete(id);
        }
        const state = store.states.get(id);
        if (state)
        {
            const children = (state as unknown as StateInternal)[writableParent]?.children;
            if (children)
            {
                const index = children.indexOf(state);
                if (index >= 0)
                {
                    (children as State<unknown, unknown, unknown>[]).splice(index, 1);
                }
            }
        }
        else
        {
            console.warn(`No state ${id} to delete`);
        }
        store.states.delete(id);
    }
}
